/**
 * Hora Advanced Agenda — public Day View calendar enhancement.
 *
 * Vanilla TS shim (no React, no jQuery). PHP ships fully rendered HTML with
 * absolute-positioned cards; this script handles:
 *  - Agenda-day tab switching.
 *  - Section card → details drawer (fetch server-rendered HTML, slide in).
 *  - Prev/Next within a panel: scroll the neighbor card into view + 2s glow.
 */
import { openSectionDrawer, pulseCard } from "../shared/section-drawer-bridge";
import { initAddToCalendarTracking } from "../shared/analytics";
import { startLiveState, formatTimeRange, } from "../shared/live-state";
import { mountNowLine } from "../shared/now-line";
import { applySectionStates, mountSectionStates, } from "../shared/section-state";
const CONTAINER_SELECTOR = '.hora-calendar[data-group-id]';
const TAB_SELECTOR = '.hora-calendar__tab[role="tab"]';
const PANEL_SELECTOR = '.hora-calendar__agenda[role="tabpanel"]';
const CARD_SELECTOR = '[data-section-id]';
const PULSE_CLASS = "hora-calendar__section--pulse";
const URL_PARAM = 'agenda';
const URL_PARAM_SECTION = 'section';
function collectTabs(container) {
    const tabs = Array.from(container.querySelectorAll(TAB_SELECTOR));
    const panels = Array.from(container.querySelectorAll(PANEL_SELECTOR));
    if (tabs.length === 0 || panels.length === 0) {
        return { container, tabs: [], panels: [] };
    }
    return { container, tabs, panels };
}
// Rewrite the agenda id in the address bar without reloading; preserves
// every other query param so a layout switch (?layout=...) still carries
// the active day across.
function syncUrl(agendaId) {
    try {
        const url = new URL(window.location.href);
        if (url.searchParams.get(URL_PARAM) === agendaId)
            return;
        url.searchParams.set(URL_PARAM, agendaId);
        window.history.replaceState({}, '', url.toString());
    }
    catch (_a) {
        /* History API unavailable — selection still works in-page */
    }
}
function activate(state, target, focus) {
    const agendaId = target.getAttribute('data-agenda-id');
    if (!agendaId) {
        return;
    }
    for (const tab of state.tabs) {
        const isActive = tab === target;
        tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
        tab.tabIndex = isActive ? 0 : -1;
    }
    for (const panel of state.panels) {
        const match = panel.getAttribute('data-agenda-id') === agendaId;
        if (match) {
            panel.removeAttribute('hidden');
        }
        else {
            panel.setAttribute('hidden', '');
        }
    }
    state.container.setAttribute('data-active-agenda', agendaId);
    syncUrl(agendaId);
    if (focus) {
        target.focus();
    }
    state.container.dispatchEvent(new CustomEvent('hora:calendar:tab-change', {
        detail: { agendaId: Number(agendaId) },
        bubbles: true,
        cancelable: true,
    }));
}
function onClick(state, evt) {
    const target = evt.target;
    if (!target)
        return;
    const tab = target.closest(TAB_SELECTOR);
    if (tab && state.tabs.indexOf(tab) !== -1) {
        evt.preventDefault();
        activate(state, tab, false);
        return;
    }
    const card = target.closest(CARD_SELECTOR);
    if (card && state.container.contains(card)) {
        // Let genuine anchors (e.g. Add-to-calendar links) keep their default behavior.
        if (target.closest('a[href]'))
            return;
        const sectionId = card.getAttribute('data-section-id');
        if (sectionId) {
            evt.preventDefault();
            openSectionDrawer(state.container, sectionId, card, PULSE_CLASS, "calendar");
        }
    }
}
function onKeydown(state, evt) {
    const target = evt.target;
    if (!target)
        return;
    const tab = target.closest(TAB_SELECTOR);
    if (tab && state.tabs.indexOf(tab) !== -1) {
        const idx = state.tabs.indexOf(tab);
        let next = null;
        switch (evt.key) {
            case 'ArrowRight':
                next = state.tabs[(idx + 1) % state.tabs.length];
                break;
            case 'ArrowLeft':
                next = state.tabs[(idx - 1 + state.tabs.length) % state.tabs.length];
                break;
            case 'Home':
                next = state.tabs[0];
                break;
            case 'End':
                next = state.tabs[state.tabs.length - 1];
                break;
            case 'Enter':
            case ' ':
                next = tab;
                break;
            default:
                return;
        }
        if (next) {
            evt.preventDefault();
            activate(state, next, true);
        }
        return;
    }
    const card = target.closest(CARD_SELECTOR);
    if (card && state.container.contains(card) && (evt.key === "Enter" || evt.key === " ")) {
        const sectionId = card.getAttribute('data-section-id');
        if (sectionId) {
            evt.preventDefault();
            openSectionDrawer(state.container, sectionId, card, PULSE_CLASS, "calendar");
        }
    }
}
function readUrlSection() {
    try {
        return new URL(window.location.href).searchParams.get(URL_PARAM_SECTION);
    }
    catch (_a) {
        return null;
    }
}
function clearUrlParam(name) {
    try {
        const url = new URL(window.location.href);
        if (!url.searchParams.has(name))
            return;
        url.searchParams.delete(name);
        window.history.replaceState({}, '', url.toString());
    }
    catch (_a) {
        /* History API unavailable */
    }
}
function cssEscape(value) {
    if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
        return CSS.escape(value);
    }
    return value.replace(/[^a-zA-Z0-9_-]/g, (ch) => `\\${ch}`);
}
// Deep-link: `?section=<id>` pre-opens the section drawer. If the section
// lives on a different agenda day than what's currently active, switch the
// day tab first so closing the drawer reveals the card. One-shot: the param
// is stripped from the URL immediately.
function openDrawerFromUrl(state) {
    const sectionId = readUrlSection();
    if (!sectionId)
        return;
    clearUrlParam(URL_PARAM_SECTION);
    const card = state.container.querySelector(`[data-section-id="${cssEscape(sectionId)}"]`);
    if (!card)
        return;
    const targetGrid = card.closest(PANEL_SELECTOR);
    const targetAgendaId = (targetGrid === null || targetGrid === void 0 ? void 0 : targetGrid.getAttribute('data-agenda-id')) || '';
    if (targetAgendaId && state.container.getAttribute('data-active-agenda') !== targetAgendaId) {
        const tab = state.tabs.find((t) => t.getAttribute('data-agenda-id') === targetAgendaId);
        if (tab)
            activate(state, tab, false);
    }
    void openSectionDrawer(state.container, sectionId, null, PULSE_CLASS, 'calendar');
}
// URL is the source of truth for the active day. If the URL names an agenda
// that exists in this calendar, activate it; otherwise sync the URL to the
// server-rendered default (cleans stale ?agenda=<id> values).
function restore(state) {
    let requested = null;
    try {
        requested = new URL(window.location.href).searchParams.get(URL_PARAM);
    }
    catch (_a) {
        requested = null;
    }
    if (requested) {
        const match = state.tabs.find((t) => t.getAttribute('data-agenda-id') === requested);
        if (match) {
            activate(state, match, false);
            return;
        }
    }
    const fallback = state.container.getAttribute('data-active-agenda');
    if (fallback) {
        syncUrl(fallback);
    }
}
/* ---------- Live state + now-line ------------------------------------ */
const GRID_SELECTOR = '.hora-calendar__grid[data-agenda-id]';
function applyCalendarDelays(grid, payload, prevDelays) {
    const rangeStart = Number(grid.getAttribute('data-range-start-min') || '0');
    const ppm = Number(grid.getAttribute('data-ppm') || '0');
    if (!ppm)
        return;
    for (const row of payload.delays) {
        const card = grid.querySelector(`.hora-calendar__section[data-section-id="${row.section_id}"]`);
        if (!card)
            continue;
        const baseStart = Number(card.getAttribute('data-start-min') || 'NaN');
        const baseEnd = Number(card.getAttribute('data-end-min') || 'NaN');
        if (!Number.isFinite(baseStart) || !Number.isFinite(baseEnd))
            continue;
        const eff = row.effective_delay_minutes | 0;
        const newStart = baseStart + eff;
        const newEnd = baseEnd + eff;
        const newTop = Math.max(0, (newStart - rangeStart) * ppm);
        const newHeight = Math.max(0, (newEnd - newStart) * ppm);
        card.style.setProperty('--hora-s-top', `${newTop}px`);
        card.style.setProperty('--hora-s-height', `${newHeight}px`);
        card.setAttribute('data-delay-min', String(row.delay_minutes | 0));
        card.setAttribute('data-effective-start-min', String(newStart));
        card.setAttribute('data-effective-end-min', String(newEnd));
        const prev = prevDelays.get(row.section_id);
        const wrapper = card.querySelector('.hora-calendar__section-time-text');
        const timeEl = wrapper === null || wrapper === void 0 ? void 0 : wrapper.querySelector('time');
        if (wrapper && timeEl) {
            const fmt = wrapper.getAttribute('data-time-format') || 'H:i';
            timeEl.textContent = formatTimeRange(newStart, newEnd, fmt);
            timeEl.dateTime = formatIso(newStart);
        }
        if (prev !== undefined && prev !== eff) {
            pulseCard(grid, String(row.section_id), 'hora-calendar__section--pulse');
        }
        prevDelays.set(row.section_id, eff);
    }
}
function formatIso(min) {
    const total = ((min % (24 * 60)) + 24 * 60) % (24 * 60);
    const h = Math.floor(total / 60);
    const m = total % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:00`;
}
function initLiveForGrid(grid) {
    const agendaId = Number(grid.getAttribute('data-agenda-id') || '0');
    const tz = grid.getAttribute('data-tz') || 'UTC';
    const agendaDate = grid.getAttribute('data-agenda-date') || '';
    const rangeStartMin = Number(grid.getAttribute('data-range-start-min') || '0');
    const rangeEndMin = Number(grid.getAttribute('data-range-end-min') || '0');
    const ppm = Number(grid.getAttribute('data-ppm') || '0');
    const serverNow = grid.getAttribute('data-server-now');
    mountNowLine({
        container: grid,
        view: 'calendar',
        tz,
        agendaDate,
        rangeStartMin,
        rangeEndMin,
        ppm,
        serverNow,
    });
    mountSectionStates({
        container: grid,
        view: 'calendar',
        tz,
        agendaDate,
        serverNow,
    });
    const cfg = window.horaCalendar;
    if (!agendaId ||
        !cfg ||
        cfg.liveEnabled === false ||
        !cfg.liveStateUrl) {
        return;
    }
    const prevDelays = new Map();
    startLiveState({
        agendaId,
        baseUrl: cfg.liveStateUrl,
        pollMs: typeof cfg.livePollMs === 'number' ? cfg.livePollMs : 8000,
        jitterMs: typeof cfg.liveJitterMs === 'number' ? cfg.liveJitterMs : 4000,
        firstTickMaxMs: typeof cfg.liveFirstTickMaxMs === 'number' ? cfg.liveFirstTickMaxMs : 500,
        onUpdate: (payload) => {
            requestAnimationFrame(() => {
                applyCalendarDelays(grid, payload, prevDelays);
                applySectionStates({
                    container: grid,
                    view: 'calendar',
                    tz,
                    agendaDate,
                    serverNow: payload.server_now,
                });
            });
        },
    });
}
/* ---------- Bootstrap ------------------------------------------------- */
function init(container) {
    const state = collectTabs(container);
    if (!state)
        return;
    container.addEventListener('click', (e) => onClick(state, e));
    container.addEventListener('keydown', (e) => onKeydown(state, e));
    initAddToCalendarTracking(container, 'calendar');
    if (state.tabs.length > 0) {
        restore(state);
    }
    openDrawerFromUrl(state);
    container.querySelectorAll(GRID_SELECTOR).forEach(initLiveForGrid);
}
function boot() {
    const containers = document.querySelectorAll(CONTAINER_SELECTOR);
    containers.forEach(init);
}
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
}
else {
    boot();
}
